import Service from '@ember/service';
import { assert } from '@ember/debug';
import { schedule } from '@ember/runloop';
const ACTIONABLE_TAG_NAMES = ['a', 'button', 'input', 'select', 'textarea'];
const ACTIONABLE_SELECTOR = [...ACTIONABLE_TAG_NAMES.map(tag => `${tag}:not([disabled])`), '[data-ember-action]'].join(',');

// If the element exists, it tries to focus on it.
// It will assert if the element does not exist or is not actionable.
function focusElement(element) {
  assert('Element must exist to be focused', element);
  if (element) {
    element.focus();
    assert('Attempted to focus on element that was not focusable', document.activeElement === element);
  }
}
export function isActionableElement(el) {
  return el.matches(ACTIONABLE_SELECTOR);
}

// Returns all element siblings between the two elements.
function findElementsBetween(firstElement, lastElement) {
  let currentElement = firstElement.nextElementSibling;
  const between = [];
  while (currentElement !== lastElement) {
    between.push(currentElement);
    currentElement = currentElement.nextElementSibling;
  }
  return between;
}

// Gets all elements that match the given selector that are either
// in the elements list or are descendents of an element in the list.
// If an element in the list matches the selector, its descendents are not searched.
function inclusiveDescendantsWithSelector(elements, selector) {
  return elements.flatMap(element => {
    if (element.matches(selector)) {
      return [element];
    } else {
      return [...element.querySelectorAll(selector)];
    }
  });
}

/*

## Focus Service:

A central authority for handling focus for dialogs. Works in conjunction with the
`{{focus-manager}}` component.

To use, wrap your component template in a `{{focus-manager}}`. When the element is inserted,
it will register itself with the focus service and focus on itself.

If one focus manager is destroyed, the previous focus manager that was created will be given the focus back.

*/
// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  focusManagers: null,
  init(...args) {
    this._super(...args);
    this.focusManagers = [];
  },
  addFocusManager(focusId, autofocusSelector) {
    this.focusManagers = [...this.focusManagers, {
      focusId,
      autofocusSelector
    }];
    this.focusOn(focusId, autofocusSelector);
  },
  removeFocusManager(focusIdToRemove) {
    this.focusManagers = this.focusManagers.filter(({
      focusId
    }) => focusId !== focusIdToRemove);

    // Attempting to focus on a hidden element doesn't work, so we give
    // the other focus manager a chance to become visible
    schedule('afterRender', () => {
      if (this.focusManagers.length > 0) {
        const {
          focusId,
          autofocusSelector
        } = this.focusManagers[this.focusManagers.length - 1];
        this.focusOn(focusId, autofocusSelector);
      }
    });
  },
  // Gets elements in this component, excluding the focus-capturing divs
  getElements(focusId) {
    const [firstElement, lastElement] = document.querySelectorAll(`[data-focus-manager-capture-focus="${focusId}"]`);
    return findElementsBetween(firstElement, lastElement);
  },
  focusOn(focusId, autofocusSelector) {
    // Attempt to focus on the passed-in selector, if it exists,
    // or the first actionable element.
    if (autofocusSelector) {
      const autofocusElements = inclusiveDescendantsWithSelector(this.getElements(focusId), autofocusSelector);
      focusElement(autofocusElements[0]);
    } else {
      this.captureFocus(focusId, 'first');
    }
  },
  captureFocus(focusId, which) {
    // if the user tabs outside of the modal (hitting either hidden div),
    // move focus back to the first or last actionable element
    const focusableElements = inclusiveDescendantsWithSelector(this.getElements(focusId), ACTIONABLE_SELECTOR);
    if (focusableElements.length > 0) {
      if (which === 'first') {
        focusableElements[0].focus();
      } else {
        focusableElements[focusableElements.length - 1].focus();
      }
    } else {
      // Do not leave the focus on the focus capture div
      document.activeElement.blur();
    }
  }
});